import { STAT_TILE_CONFIGS } from "../constants";
import { ReadinessRiskLevel } from "../types/dashboard";

export type DashboardTileKey = (typeof STAT_TILE_CONFIGS)[number]["key"];

type FilterOperator = "eq" | "not_eq" | "gteq" | "lteq" | "gt" | "lt" | "matches";

interface FilterParam {
  field: string;
  operator: FilterOperator;
  value: string;
}

export interface TileLinkFilterSettings {
  filters: string[];
  globalOperator: string;
  practitioner: boolean;
}

export interface TileLink {
  path: string;
  filterSettings: TileLinkFilterSettings | null;
}

export type SurgeryPreset =
  | "this_week"
  | "yesterday"
  | "today"
  | "tomorrow"
  | "high_risk"
  | "medium_risk"
  | "low_risk"
  | "no_surgery_date"
  | "surgery_past"
  | "upcoming"
  | "completed"
  | "completed_30_plus"
  | "pending_activation"
  | "not_activated_3_days";

/** Activation presets list the longest-waiting referral first. */
export const ACTIVATION_PRESETS: readonly SurgeryPreset[] = [
  "pending_activation",
  "not_activated_3_days",
];
export const ACTIVATION_PRESET_SORT = ["created_at"];

export const isActivationPreset = (preset: string | null | undefined): boolean =>
  ACTIVATION_PRESETS.includes(preset as SurgeryPreset);

// --- Date helpers (local timezone, not UTC) ---

export const formatDate = (date: Date): string => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

const today = (currentDate: Date = new Date()): string => formatDate(currentDate);

const tomorrow = (currentDate: Date = new Date()): string => {
  const d = new Date(currentDate);
  d.setDate(d.getDate() + 1);
  return formatDate(d);
};

/** Monday-Sunday ISO week, matching Rails Date#all_week */
const getWeekRange = (currentDate: Date = new Date()): { start: string; end: string } => {
  const d = new Date(currentDate);
  const day = d.getDay();
  const diffToMonday = day === 0 ? -6 : 1 - day;
  const monday = new Date(d);
  monday.setDate(d.getDate() + diffToMonday);
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  return { start: formatDate(monday), end: formatDate(sunday) };
};

// --- Filter builders ---

const buildFilterSettings = (filters: FilterParam[]): TileLinkFilterSettings => ({
  filters: filters.map((f) => JSON.stringify(f)),
  globalOperator: "and",
  practitioner: false,
});

const buildReadinessFilter = (
  level: ReadinessRiskLevel
): TileLinkFilterSettings =>
  buildFilterSettings([{ field: "readiness", operator: "eq", value: level }]);

const buildSurgeryPresetLink = (preset: SurgeryPreset): TileLink => ({
  path: `/patients?page=1&preset=${preset}`,
  filterSettings: null,
});

// --- Public API ---

export const getTileLinkForKey = (key: DashboardTileKey): TileLink => {
  switch (key) {
    case "surgeries_week":
      return buildSurgeryPresetLink("this_week");
    case "surgeries_today":
      return buildSurgeryPresetLink("today");
    case "surgeries_tomorrow":
      return buildSurgeryPresetLink("tomorrow");
    case "pending_activation":
      return buildSurgeryPresetLink("pending_activation");
    case "high_risk_patients":
      return buildSurgeryPresetLink("high_risk");
  }
};

export const getSurgeryPresetFilters = (
  preset: SurgeryPreset,
  currentDate: Date = new Date()
): TileLinkFilterSettings => {
  switch (preset) {
    case "this_week": {
      const week = getWeekRange(currentDate);
      return buildFilterSettings([
        { field: "surgery_date", operator: "gteq", value: week.start },
        { field: "surgery_date", operator: "lteq", value: week.end },
      ]);
    }
    case "yesterday": {
      const d = new Date(currentDate);
      d.setDate(d.getDate() - 1);
      return buildFilterSettings([{ field: "surgery_date", operator: "eq", value: formatDate(d) }]);
    }
    case "today":
      return buildFilterSettings([{ field: "surgery_date", operator: "eq", value: today(currentDate) }]);
    case "tomorrow":
      return buildFilterSettings([
        { field: "surgery_date", operator: "eq", value: tomorrow(currentDate) },
      ]);
    case "high_risk":
      return buildReadinessFilter("high");
    case "medium_risk":
      return buildReadinessFilter("medium");
    case "low_risk":
      return buildReadinessFilter("low");
    case "no_surgery_date":
      return buildReadinessFilter("no_surgery_date");
    case "surgery_past":
      return buildReadinessFilter("surgery_past");
    case "upcoming":
      return buildFilterSettings([
        { field: "surgery_date", operator: "gteq", value: today(currentDate) },
      ]);
    case "completed":
      return buildFilterSettings([
        { field: "patient_status", operator: "eq", value: "Surgery Completed" },
      ]);
    case "pending_activation":
      return buildFilterSettings([
        { field: "activation_status", operator: "eq", value: "pending" },
      ]);
    case "not_activated_3_days":
      return buildFilterSettings([
        { field: "activation_status", operator: "eq", value: "overdue" },
      ]);
    case "completed_30_plus": {
      const cutoff = new Date(currentDate);
      cutoff.setDate(cutoff.getDate() - 31);
      return buildFilterSettings([
        { field: "surgery_status", operator: "eq", value: "Completed" },
        { field: "surgery_date", operator: "lteq", value: formatDate(cutoff) },
      ]);
    }
  }
};
